import React, { useState, useEffect, useMemo, useCallback } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { useNavigate } from "react-router-dom";
import { Building, Shield, Lock } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../contexts/AuthContext";
import { useHasLicense } from "../contexts/LicenseContext";
import { useRBAC } from "../hooks/useRBAC";
import { useShowExecutiveReport } from "../contexts/FeatureFlagsContext";
import { fusionCellCount } from "./fusion/useFusionSummary";
import Navigation from "../components/Navigation";
import Card from "../components/Card";
import LoadingSpinner from "../components/LoadingSpinner";
import { CheckCircle, AlertTriangle, X, Search, Edit, FileText } from "lucide-react";
import ReactSpeedometer from "react-d3-speedometer";
import IntelligenceNarrative from "./IntelligenceNarrative";
import ExposureTrajectoryChart from "./ExposureTrajectoryChart";
import AttackPathsCard from "./AttackPathsCard";
import { severityPostureScore, severityPostureLevel } from '../utils/riskScoringEngine';
import { useLiveDashboard } from '../hooks/useLiveDashboard';


// Helper: compute weighted risk score from distribution counts
// allTotal: total risks including remediated (used as denominator so score always drops when risks remediated)
const computeWeightedScore = (dist = {}, allTotal = 0) => severityPostureScore(dist);

// ── Shared design helpers ──────────────────────────────────────────────────────
const getRiskBadgeClass = (level) => {
    switch (level) {
        case 'CRITICAL': return 'bg-red-50 text-red-700 border-red-200';
        case 'HIGH':     return 'bg-orange-50 text-orange-600 border-orange-200';
        case 'MEDIUM':   return 'bg-yellow-50 text-yellow-700 border-yellow-200';
        case 'LOW':      return 'bg-green-50 text-green-600 border-green-200';
        default:         return 'bg-gray-50 text-gray-500 border-gray-200';
    }
};

const getScoreTextColor = (level) => {
    switch (level) {
        case 'CRITICAL': return 'text-red-700';
        case 'HIGH':     return 'text-orange-600';
        case 'MEDIUM':   return 'text-yellow-600';
        case 'LOW':      return 'text-green-600';
        default:         return 'text-gray-500';
    }
};

const getDealTextColor = (level) => getScoreTextColor(level);

const getDealLeftBorderColor = (level) => {
    switch (level) {
        case 'CRITICAL': return 'bg-red-700';
        case 'HIGH':     return 'bg-orange-500';
        case 'MEDIUM':   return 'bg-yellow-500';
        case 'LOW':      return 'bg-green-500';
        default:         return 'bg-gray-400';
    }
};

// Section heading with left accent bar
const SectionTitle = ({ children }) => (
    <div className="flex items-center gap-2">
        <span className="w-1 h-5 rounded-full flex-shrink-0" style={{ background: 'linear-gradient(to bottom, #0ea5e9, #8b5cf6)' }} />
        <span className="text-base font-bold text-gray-900">{children}</span>
    </div>
);

// ── Overview section placeholder ───────────────────────────────────────────────
// Shown in place of a section that renders dashboard-overview figures until that read settles, so the
// rest of the page can load in parallel without showing zero counts in the meantime.
const OverviewSectionPlaceholder = ({ testId, className = "" }) => (
    <div
        data-testid={testId}
        role="status"
        aria-label="Loading"
        className={`bg-white border border-gray-200 rounded-2xl shadow-sm animate-pulse ${className}`}
    />
);

// ── Vulnerability Distribution Strip (Overall Risk Posture style) ──────────────
const VulnerabilityDistributionStrip = ({ data }) => {
    const { risk_distribution = {}, total_risks = 0 } = data || {};

    const segments = [
        { label: 'Low',      count: risk_distribution.LOW      || 0, bg: 'bg-green-500',  txt: 'text-green-700'  },
        { label: 'Medium',   count: risk_distribution.MEDIUM   || 0, bg: 'bg-yellow-500', txt: 'text-yellow-700' },
        { label: 'High',     count: risk_distribution.HIGH     || 0, bg: 'bg-orange-500', txt: 'text-orange-700' },
        { label: 'Critical', count: risk_distribution.CRITICAL || 0, bg: 'bg-red-700',    txt: 'text-red-700'    },
    ];

    return (
        <div className="mb-4 bg-white border border-gray-200 rounded-xl px-6 py-4 shadow-sm flex items-center gap-5">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest whitespace-nowrap">
                Vulnerability<br />Distribution
            </div>
            <div className="flex-1 min-w-0">
                {total_risks > 0 ? (
                    <>
                        {/* Per-segment count + label aligned above each coloured slice */}
                        <div className="flex mb-1">
                            {segments.map(({ label, count, txt }) => {
                                if (count === 0) return null;
                                const pct = (count / total_risks) * 100;
                                return (
                                    <div key={label} style={{ width: `${pct}%` }} className="flex flex-col items-center overflow-hidden px-0.5">
                                        <span className={`text-xs font-extrabold leading-none ${txt}`}>{count}</span>
                                        <span className="text-[9px] text-gray-400 leading-none mt-0.5 truncate w-full text-center">{label}</span>
                                    </div>
                                );
                            })}
                        </div>
                        {/* Stacked proportional bar */}
                        <div className="flex h-3 rounded-full overflow-hidden">
                            {segments.map(({ label, count, bg }) => count > 0 && (
                                <div key={label} className={`${bg} transition-all duration-1000`} style={{ width: `${(count / total_risks) * 100}%` }} />
                            ))}
                        </div>
                    </>
                ) : (
                    <div className="h-3 rounded-full bg-gray-100" />
                )}
            </div>
            <div className="text-center flex-shrink-0 border-l border-gray-200 pl-4">
                <div className="text-lg font-extrabold text-gray-900 leading-none">{total_risks}</div>
                <div className="text-xs text-gray-400 mt-0.5">Active</div>
            </div>
        </div>
    );
};

// ── Shared Module Card ─────────────────────────────────────────────────────────
const ModuleCard = ({ name, icon, sub, route, score, total, cta }) => {
    const navigate = useNavigate();
    const level = severityPostureLevel(score);

    return (
        <div
            className="bg-white border border-gray-200 rounded-2xl p-5 cursor-pointer relative overflow-hidden transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:border-gray-300"
            onClick={() => navigate(route)}
        >
            <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-2xl" style={{ background: 'linear-gradient(90deg, #0ea5e9, #8b5cf6)' }} />
            <div className="flex items-start justify-between mb-3 mt-1">
                <div>
                    <div className="font-bold text-gray-900 text-sm">{name}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{sub}</div>
                </div>
                <div className="w-10 h-10 flex items-center justify-center bg-gray-50 border border-gray-200 rounded-xl text-lg flex-shrink-0">{icon}</div>
            </div>
            <div className={`text-3xl font-extrabold tracking-tight leading-none mb-3 ${getScoreTextColor(level)}`}>{total > 0 ? `${score}%` : '—'}</div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden border border-gray-100 mb-3">
                <div
                    className="h-full rounded-full transition-all duration-1000"
                    style={{ width: `${score}%`, background: 'linear-gradient(90deg, #0ea5e9, #8b5cf6)' }}
                />
            </div>
            {total > 0 ? (
                <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">{total} active risks</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${getRiskBadgeClass(level)}`}>{level}</span>
                </div>
            ) : (
                <div className="text-xs text-gray-400 italic">Assessment Not Started</div>
            )}
            <div className="mt-2 text-xs text-gray-400">→ {cta || `View ${name} Dashboard`}</div>
        </div>
    );
};

const Dashboard = () => {
    const [analytics, setAnalytics] = useState(null);

    const apiCall = useApi();
    const { user, logout } = useAuth();
    const hasLicense = useHasLicense();
    const navigate = useNavigate();

    const showDeals = hasLicense('Deals');
    const { canViewOrganizationAssessments, canManageOrganizationRisks, canViewOrganization } = useRBAC();
    const showExecutiveReport = useShowExecutiveReport();

    // Universal Fusion is hidden on the dashboard for now (2026-10-19): its summary was the slowest
    // call on the page (25 s median on staging). It stays on its own /Fusion page. To restore it,
    // bring back useFusion/useFusionSummary, the heatmap's fusionSummary/onFusionCell props and the
    // FusionPanel under the chart row.

    const [cumulativeData, setCumulativeData] = useState(null);
    const [generatingReport, setGeneratingReport] = useState(false);

    // Org-wide Executive Report PDF — same base64 download flow as the assessment page's
    // Executive Summary button (AssessmentRisksView.exportExecutiveSummary).
    const exportExecutiveReport = async () => {
        if (generatingReport) return;
        setGeneratingReport(true);
        try {
            const response = await apiCall('GET', '/allRisks/executive-report-pdf');
            if (response.pdf_data && response.filename) {
                const link = document.createElement('a');
                link.href = `data:application/pdf;base64,${response.pdf_data}`;
                link.download = response.filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                throw new Error('No valid report data received');
            }
        } catch (error) {
            console.error('Error generating executive report:', error);
            alert('Failed to generate the Executive Report. Please try again.');
        } finally {
            setGeneratingReport(false);
        }
    };
    const [loadingCumulative, setLoadingCumulative] = useState(true);
    const [predictiveRisk, setPredictiveRisk] = useState({ day45: 0, day90: 0, day180: 0 });
    const [loadingPredictive, setLoadingPredictive] = useState(true);
    const [predictiveModules, setPredictiveModules] = useState([]);

    // Raw (unscaled) predictive — scaled by cumulative remediation ratio in derived useEffect
    const [rawPredictive, setRawPredictive] = useState({ day45: 0, day90: 0, day180: 0 });

    // Constructed reference estate: the backend sends the notice sentence with the overview and it is
    // shown verbatim — never re-typed here, so the dashboard and the exported report cannot disagree.
    // Null (no banner) when the flag is false/absent, including every response predating the field.
    const [constructedNotice, setConstructedNotice] = useState(null);

    // Redirect to tenant selection if no organization is selected
    useEffect(() => {
        const org = JSON.parse(localStorage.getItem("selectedOrganization"));
        if (!org?.organizationId) {
            navigate('/organizations');
        }
    }, [navigate]);

    // Cumulative risk + predictive risk for all modules.
    // Single backend read (/allRisks/dashboard-overview) loads the org's unified risk graph ONCE and
    // returns every per-module vulnerability distribution + org-level predictive score, replacing the
    // ~18 separate per-module round-trips this dashboard used to make. Enterprise predictive stays its
    // own (assessment-scoped) call.
    // `silent` skips the loading-state toggles so the live background refresh swaps numbers in place
    // with no spinner/flicker; the mount call passes silent=false for the normal first-load spinner.
    const fetchDashboardOverview = useCallback(async (silent = false) => {
            const org = JSON.parse(localStorage.getItem("selectedOrganization"));
            if (!org?.organizationId) return;

            let overview = null;
            try {
                overview = await apiCall('GET', '/allRisks/dashboard-overview');
            } catch { /* overview unavailable — render with empty totals */ }
            // Only a successful read moves the label; a failed (e.g. silent live) refresh keeps it.
            if (overview) {
                setConstructedNotice(
                    overview.constructedEstate === true && overview.constructedEstateNotice
                        ? overview.constructedEstateNotice
                        : null);
            }
            const vuln = overview?.vulnerabilityDistribution || {};
            const pred = overview?.predictiveRisk || {};

            // ── Cumulative distribution ──────────────────────────────────────
            const cumCounts = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 };
            let cumAllCount = 0;  // sum of all_risks across modules (including remediated, for score denominator)
            const moduleStatus = [];

            // Helper: add { risk_distribution, total_risks, all_risks } shaped entry
            const addStandardDist = (name, data) => {
                if (!data) return;
                const dist = data.risk_distribution || {};
                const activeTotal = data.total_risks || 0;
                const allTotal = data.all_risks || activeTotal; // falls back to activeTotal for non-AI modules
                if (allTotal > 0) {
                    ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].forEach(k => { cumCounts[k] += (dist[k] || 0); });
                    cumAllCount += allTotal;
                    moduleStatus.push({
                        name,
                        score: computeWeightedScore(dist, allTotal),
                        total: activeTotal,
                        riskCounts: { CRITICAL: dist.CRITICAL || 0, HIGH: dist.HIGH || 0, MEDIUM: dist.MEDIUM || 0, LOW: dist.LOW || 0 },
                    });
                }
            };

            // Helper: merge several { risk_distribution, total_risks, all_risks } entries into one.
            const addCombinedDist = (name, dataList) => {
                const merged = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 };
                let activeTotal = 0;
                let allTotal = 0;
                dataList.forEach(data => {
                    if (!data) return;
                    const dist = data.risk_distribution || {};
                    ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].forEach(k => { merged[k] += (dist[k] || 0); });
                    activeTotal += data.total_risks || 0;
                    allTotal += data.all_risks || data.total_risks || 0;
                });
                if (allTotal > 0) {
                    ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].forEach(k => { cumCounts[k] += merged[k]; });
                    cumAllCount += allTotal;
                    moduleStatus.push({
                        name,
                        score: computeWeightedScore(merged, allTotal),
                        total: activeTotal,
                        riskCounts: { ...merged },
                    });
                }
            };

            // Enterprise: roll the deal-level distribution ("Enterprise:deal") into the org-level "Ent"
            // entry so Enterprise shows as a single combined column/card (deal counts included).
            addCombinedDist('Ent', [vuln['Enterprise:org'], vuln['Enterprise:deal']]);

            // Standard module distributions, keyed into the overview response. "External" surfaces the
            // integration-sourced risks (ESM, secrets, dark-web exposure, ransomware-live, telegram) as
            // its own "Ext" heatmap column + module card.
            const standardModules = [
                { name: 'Business Unit', key: 'BusinessUnits' },
                { name: 'Deal',          key: 'Deals' },
                { name: 'Ransomware',    key: 'Ransomware' },
                { name: 'Dark Web',      key: 'DarkWeb' },
                { name: 'AI Risk',       key: 'AI' },
                { name: 'TPRM',          key: 'TPRM' },
                { name: 'GRC',           key: 'GRC' },
                { name: 'External',      key: 'External' },
            ];
            standardModules.forEach(m => addStandardDist(m.name, vuln[m.key]));

            // Grand totals: prefer the org-wide DEDUPED distribution so cross-listed tagged risks
            // (counted under each tag by the per-module distributions above) aren't multiplied. The summed
            // per-module counts above remain the fallback if it's absent, and the per-module
            // `moduleStatus` (heatmap) deliberately keeps its tag-aware per-module counts.
            const orgDist = vuln['All'];
            if (orgDist) {
                const d = orgDist.risk_distribution || {};
                ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].forEach(k => { cumCounts[k] = d[k] || 0; });
                cumAllCount = orgDist.all_risks ?? orgDist.total_risks ?? cumAllCount;
            }

            const totalRisks = Object.values(cumCounts).reduce((a, b) => a + b, 0);
            const overallScore = computeWeightedScore(cumCounts, cumAllCount);
            const overallLevel = severityPostureLevel(overallScore);

            setCumulativeData({
                overall_risk_score: overallScore,
                overall_RiskLevel: overallLevel,
                total_risks: totalRisks,      // active risks (after remediation)
                all_risks: cumAllCount,        // total risks including remediated
                risk_distribution: cumCounts,
                module_status: moduleStatus,
            });
            if (!silent) setLoadingCumulative(false);

            // ── Cumulative predictive risk — risk-weighted average by each module's share of active risks ──
            if (!silent) setLoadingPredictive(true);
            try {
                const totalActive = totalRisks || 0;

                // Build risk-share map: module name → fraction of total active risks
                const riskShare = {};
                moduleStatus.forEach(m => {
                    if ((m.total || 0) > 0) riskShare[m.name] = m.total / totalActive;
                });

                // Predictive scores from the overview, keyed to module display names.
                const modulesPred = {};
                const predModules = [
                    { name: 'Ransomware',    key: 'Ransomware' },
                    { name: 'Dark Web',      key: 'DarkWeb' },
                    { name: 'AI Risk',       key: 'AI' },
                    { name: 'Deal',          key: 'Deals' },
                    { name: 'Business Unit', key: 'BusinessUnits' },
                    { name: 'TPRM',          key: 'TPRM' },
                    { name: 'GRC',           key: 'GRC' },
                    { name: 'External',      key: 'External' },
                ];
                predModules.forEach(m => {
                    const d = pred[m.key];
                    if (d) {
                        modulesPred[m.name] = {
                            day45:  d.predictive45DayScore  || 0,
                            day90:  d.predictive90DayScore  || 0,
                            day180: d.predictive180DayScore || 0,
                        };
                    }
                });

                // Enterprise (assessment-based) — kept as its own call (different computation).
                try {
                    const summary = await apiCall('GET', '/enterpriseOrganizations/assessment-summary');
                    const assessmentId = summary?.latestAssessmentId;
                    if (assessmentId) {
                        const epred = await apiCall('GET', `/enterpriseRisks/enterpriseAssessment/${assessmentId}/predictive-risk`);
                        modulesPred['Enterprise'] = {
                            day45:  epred?.predictive45DayScore  || 0,
                            day90:  epred?.predictive90DayScore  || 0,
                            day180: epred?.predictive180DayScore || 0,
                        };
                    }
                } catch { /* enterprise not available */ }

                // Risk-weighted average (normalized by total weight actually used)
                let wDay45 = 0, wDay90 = 0, wDay180 = 0, totalWeight = 0;
                const activeNames = [];
                Object.entries(modulesPred).forEach(([name, p]) => {
                    const share = riskShare[name] || 0;
                    if (share > 0) {
                        wDay45  += p.day45  * share;
                        wDay90  += p.day90  * share;
                        wDay180 += p.day180 * share;
                        totalWeight += share;
                        activeNames.push(name);
                    }
                });

                if (totalWeight > 0) {
                    // Normalise: if some modules have risks but no predictive data,
                    // their weight is spread across modules that do have data
                    setRawPredictive({
                        day45:  Math.round(wDay45  / totalWeight),
                        day90:  Math.round(wDay90  / totalWeight),
                        day180: Math.round(wDay180 / totalWeight),
                    });
                    setPredictiveModules(activeNames);
                } else {
                    // Fallback: unweighted average when no module has risks yet
                    const vals = Object.values(modulesPred);
                    const avg = arr => arr.length > 0 ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : 0;
                    setRawPredictive({
                        day45:  avg(vals.map(v => v.day45).filter(Boolean)),
                        day90:  avg(vals.map(v => v.day90).filter(Boolean)),
                        day180: avg(vals.map(v => v.day180).filter(Boolean)),
                    });
                    setPredictiveModules(Object.keys(modulesPred).filter(k => modulesPred[k].day45 > 0));
                }
            } catch (err) {
                setRawPredictive({ day45: 0, day90: 0, day180: 0 });
            } finally {
                if (!silent) setLoadingPredictive(false);
            }
    }, [apiCall]);

    // First load (with spinners), then keep the dashboard live: the change-token poll re-runs the
    // fetch silently whenever risks/remediations change, with no visible loading state.
    useEffect(() => { fetchDashboardOverview(false); }, [fetchDashboardOverview]);
    const liveRefresh = useCallback(() => fetchDashboardOverview(true), [fetchDashboardOverview]);
    useLiveDashboard({ refresh: liveRefresh });

    // Derived: pass through risk-table-based predictive data (already excludes remediated risks)
    useEffect(() => {
        setPredictiveRisk({
            day45: rawPredictive.day45,
            day90: rawPredictive.day90,
            day180: rawPredictive.day180,
        });
    }, [rawPredictive]);

    // Spinner only while no organization is selected (redirect in progress). The page no longer waits
    // for the dashboard-overview read: every card mounts at once and fetches in parallel, and the
    // sections that render overview figures show their own loading state until it arrives.
    const org = JSON.parse(localStorage.getItem("selectedOrganization"));
    if (!org?.organizationId) return <LoadingSpinner />;

    return (
        <div className="min-h-screen bg-theme-primary">
            <Navigation />

            <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
                {/* ── Constructed reference estate notice (persistent, not dismissible) ── */}
                {constructedNotice && (
                    <div role="note" data-testid="constructed-estate-notice" className="mb-4 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
                        <span>{constructedNotice}</span>
                    </div>
                )}

                {/* ── Hero Banner ──────────────────────────────────────────── */}
                <div className="mb-6 bg-white border border-gray-200 rounded-2xl p-8 shadow-sm relative overflow-hidden">
                    <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 50% 100% at 0% 50%, rgba(14,165,233,0.06) 0%, transparent 60%), radial-gradient(ellipse 30% 80% at 100% 50%, rgba(139,92,246,0.05) 0%, transparent 60%)' }} />
                    <div className="relative z-10 flex items-center justify-between flex-wrap gap-6">
                        <div>
                            <div className="inline-flex items-center gap-1.5 bg-sky-50 border border-sky-200 rounded-full px-3 py-1 text-xs font-semibold text-sky-600 mb-3">
                                <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse" />
                                {org?.organizationName || 'Dashboard'} &nbsp;·&nbsp; Live
                            </div>
                            <h1 className="text-2xl font-extrabold text-pulple-900 tracking-tight">
                                AXARIS <span className="text-sky-500">Dashboard</span>
                            </h1>
                            <p className="text-gray-500 text-sm mt-1">Comprehensive organizational risk overview — updated just now</p>
                        </div>
                        <div className="flex items-center">
                            <button
                                type="button"
                                onClick={() => navigate('/risks?status=active')}
                                title="View all active risks"
                                className="text-center px-6 focus:outline-none"
                            >
                                <div className="text-3xl font-extrabold text-red-600 tracking-tight leading-none">{cumulativeData?.total_risks ?? '—'}</div>
                                <div className="text-xs text-gray-400 mt-1 font-medium">Active Risks</div>
                            </button>
                            <div className="w-px h-11 bg-gray-200" />
                            <button
                                type="button"
                                onClick={() => navigate('/risks?status=active&severity=CRITICAL')}
                                title="View active critical risks"
                                className="text-center px-6 focus:outline-none"
                            >
                                <div className="text-3xl font-extrabold text-red-600 tracking-tight leading-none">{cumulativeData?.risk_distribution?.CRITICAL ?? '—'}</div>
                                <div className="text-xs text-gray-400 mt-1 font-medium">Critical</div>
                            </button>
                            <div className="w-px h-11 bg-gray-200" />
                            <div className="text-center px-6">
                                <div className="text-3xl font-extrabold text-sky-500 tracking-tight leading-none">{cumulativeData?.module_status?.length ?? '—'}</div>
                                <div className="text-xs text-gray-400 mt-1 font-medium">Modules Active</div>
                            </div>
                            <div className="w-px h-11 bg-gray-200" />
                            <div className="text-center px-6">
                                <div className={`text-3xl font-extrabold tracking-tight leading-none ${getScoreTextColor(cumulativeData?.overall_RiskLevel)}`}>{cumulativeData?.total_risks > 0 ? `${cumulativeData.overall_risk_score}%` : '—'}</div>
                                <div className="text-xs text-gray-400 mt-1 font-medium">Overall Score</div>
                            </div>
                        </div>
                        {showExecutiveReport && canViewOrganization(org?.organizationId) && (
                            <button
                                type="button"
                                onClick={exportExecutiveReport}
                                disabled={generatingReport}
                                title="Download the organization-wide Executive Report (PDF) — posture, AI summary, exposure trajectory, and top attack paths"
                                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed shadow-sm focus:outline-none"
                            >
                                {generatingReport ? (
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : (
                                    <FileText className="h-4 w-4" />
                                )}
                                {generatingReport ? 'Generating…' : 'Executive Report'}
                            </button>
                        )}
                    </div>
                </div>

                {/* Intelligence Narrative — AI briefing across all 7 risk modules (RISK_MANAGER+ at ORG) */}
                {canManageOrganizationRisks() && (
                    <IntelligenceNarrative
                        type="main"
                        title="Intelligence Narrative"
                        subtitle="AI-generated briefing across all organization risk modules"
                        accentColor="blue"
                    />
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <RiskRemediationTrendCard cumulativeData={cumulativeData} overviewLoading={loadingCumulative} />
                    <RiskHeatmapCard
                        moduleStatus={cumulativeData?.module_status || []}
                        loading={loadingCumulative}
                    />
                    <RemediationDonutCard />
                    <CumulativePredictiveRiskBars data={predictiveRisk} loading={loadingPredictive} modules={predictiveModules} cumulativeData={cumulativeData} />
                </div>

                {/* Vulnerability Distribution Strip — placeholder until the overview arrives, so it
                    never flashes zero counts. */}
                {loadingCumulative
                    ? <OverviewSectionPlaceholder testId="vulnerability-strip-loading" className="h-20 mb-4" />
                    : <VulnerabilityDistributionStrip data={cumulativeData} />}

                {/* Exposure Trajectory + Top Attack Paths */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                    <ExposureTrajectoryChart />
                    <AttackPathsCard />
                </div>

                {/* Module Dashboard Summary - Clickable Charts */}
                <div className="mb-4">
                    <div className="flex items-center justify-between mb-3">
                        <SectionTitle>Module Dashboards</SectionTitle>
                    </div>
                    {loadingCumulative ? (
                        <OverviewSectionPlaceholder testId="module-cards-loading" className="h-32" />
                    ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Same licence gates as the navigation menu — a module the org doesn't
                            subscribe to has no dashboard to link to. */}
                        {hasLicense('Enterprise') && <EnterpriseModuleCard moduleStatus={cumulativeData?.module_status} />}
                        {hasLicense('Ransomware') && <RansomwareModuleCard moduleStatus={cumulativeData?.module_status} />}
                        {hasLicense('DarkWeb') && <DarkWebModuleCard moduleStatus={cumulativeData?.module_status} />}
                        {hasLicense('AI') && <AIModuleCard moduleStatus={cumulativeData?.module_status} />}
                        {hasLicense('TPRM') && <TPRMModuleCard moduleStatus={cumulativeData?.module_status} />}
                        {hasLicense('GRC') && <GRCModuleCard moduleStatus={cumulativeData?.module_status} />}
                        <ExternalRisksModuleCard moduleStatus={cumulativeData?.module_status} />
                    </div>
                    )}
                </div>

                {/* Organization Assessment - Enterprise level */}
                {canViewOrganizationAssessments() && (
                    <div className="mb-4">
                        <OrganizationAssessmentCard />
                    </div>
                )}

                {/* Top 5 Recent Deals - Tile Format - Moved above Remediation Tracker */}
                {showDeals && (
                    <div className="mb-4">
                        <RecentDealsCard />
                    </div>
                )}

                {/* Recent Activities - hidden until dynamic data is implemented */}
                {/* <div className="mb-4">
                    <RecentActivityCard analytics={analytics} />
                </div> */}
            </div>
        </div>
    );
};


const RiskRemediationTrendCard = ({ cumulativeData, overviewLoading = false }) => {
    const apiCall = useApi();
    const [trendData, setTrendData]     = useState([]);
    const [loading, setLoading]         = useState(true);
    const [monthFilter, setMonthFilter] = useState(6);
    const [visible, setVisible]         = useState({ Risks: true, Remediated: true });

    // `silent` skips the loading toggle so the live background refresh updates the chart in place
    // with no "Loading trend…" flash; the mount / month-filter change passes silent=false.
    const fetchTrend = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const data = await apiCall('GET', `/allRemediations/trend?months=${monthFilter}`);
            setTrendData(Array.isArray(data) ? data : []);
        } catch {
            setTrendData([]);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [apiCall, monthFilter]);

    useEffect(() => { fetchTrend(false); }, [fetchTrend]);

    // Keep the trend live alongside the rest of the dashboard (this card fetches independently).
    const liveRefreshTrend = useCallback(() => fetchTrend(true), [fetchTrend]);
    useLiveDashboard({ refresh: liveRefreshTrend });

    // Map all points; thin x-axis labels to ~16 ticks via Recharts interval
    const chartData = useMemo(() =>
        trendData.map(p => ({ date: p.date, Risks: p.risks, Remediated: p.remediated })),
    [trendData]);

    // Show a dot on each point when there are few (short date range); hide when dense
    const showDots   = chartData.length <= 10;
    // x-axis label interval: show ~12 evenly-spaced labels
    const tickInterval = Math.max(0, Math.floor(chartData.length / 12) - 1);

    // Build the list of visible tick dates (every tickInterval+1 points) so we can detect month changes
    const visibleTicks = chartData.filter((_, i) => i % (tickInterval + 1) === 0).map(d => d.date);

    // Custom X-axis tick: show day number every tick, month label only on first tick of each month
    const TrendXTick = ({ x, y, payload, index }) => {
        const parts = (payload.value || '').split(' '); // e.g. ["Mar", "27"]
        const month = parts[0];
        const day   = parts[1] || '';
        const prevMonth = index > 0 ? (visibleTicks[index - 1] || '').split(' ')[0] : null;
        const showMonth = index === 0 || month !== prevMonth;
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={0} dy={12} textAnchor="middle" fill="#94a3b8" fontSize={10}>{day}</text>
                {showMonth && (
                    <text x={0} y={0} dy={24} textAnchor="middle" fill="#64748b" fontSize={9} fontWeight="600">{month}</text>
                )}
            </g>
        );
    };

    const lastPoint      = trendData[trendData.length - 1];
    // "Total risks" means every risk regardless of remediation status. cumulativeData.all_risks is
    // built from the same /allRisks/dashboard-overview data the heat map uses (its all-inclusive
    // total, not the active-only total_risks), so the count stays consistent with the rest of the
    // dashboard while still reflecting the full population of risks.
    const totalRisks     = cumulativeData?.all_risks ?? cumulativeData?.total_risks ?? lastPoint?.risks ?? 0;
    const completedCount = lastPoint?.remediated ?? 0;
    const resolvedPct    = totalRisks > 0 ? Math.round((completedCount / totalRisks) * 100) : 0;
    // The chart can render before the overview arrives (cards load in parallel); hold the totals back
    // until then so "total risks" doesn't show the trend's own count and then jump.
    const totalsReady    = !overviewLoading;

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        return (
            <div className="bg-white border border-gray-200 rounded-xl shadow-lg px-4 py-3 text-xs">
                <p className="font-semibold text-gray-600 mb-2">{label}</p>
                {payload.map(p => (
                    <div key={p.dataKey} className="flex items-center justify-between gap-4">
                        <span className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color }} />
                            <span className="text-gray-500">{p.name}</span>
                        </span>
                        <span className="font-bold" style={{ color: p.color }}>{p.value}</span>
                    </div>
                ))}
                {payload.length === 2 && payload[0].value > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-100 text-gray-400">
                        {Math.round((payload[1].value / payload[0].value) * 100)}% resolved
                    </div>
                )}
            </div>
        );
    };

    return (
        <Card className="mb-4">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <div>
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Risk &amp; Remediation Trend</div>
                    {!loading && totalsReady && totalRisks > 0 && (
                        <div className="text-xs text-gray-400 mt-0.5">{resolvedPct}% resolved · {totalRisks} total risks</div>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setVisible(v => ({ ...v, Risks: !v.Risks }))}
                        className={`px-3 py-1 rounded-full text-xs font-semibold border cursor-pointer select-none transition-all duration-150 hover:scale-105 hover:shadow active:scale-95 ${visible.Risks ? 'bg-rose-500 border-rose-500 text-white hover:bg-rose-600' : 'bg-white border-gray-300 text-gray-400 hover:border-rose-300 hover:text-rose-400'}`}
                    >
                        Risks {totalsReady ? totalRisks : '—'}
                    </button>
                    <button
                        onClick={() => setVisible(v => ({ ...v, Remediated: !v.Remediated }))}
                        className={`px-3 py-1 rounded-full text-xs font-semibold border cursor-pointer select-none transition-all duration-150 hover:scale-105 hover:shadow active:scale-95 ${visible.Remediated ? 'bg-sky-500 border-sky-500 text-white hover:bg-sky-600' : 'bg-white border-gray-300 text-gray-400 hover:border-sky-300 hover:text-sky-400'}`}
                    >
                        Remediated {completedCount}
                    </button>
                    <select
                        value={monthFilter}
                        onChange={e => setMonthFilter(Number(e.target.value))}
                        className="text-xs border border-gray-200 rounded-lg px-2 py-1 text-gray-600 bg-white"
                    >
                        <option value={3}>3M</option>
                        <option value={6}>6M</option>
                        <option value={12}>12M</option>
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-44 text-sm text-gray-300">
                    <svg className="animate-spin h-5 w-5 mr-2 text-indigo-400" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Loading trend…
                </div>
            ) : chartData.length === 0 ? (
                <div className="flex items-center justify-center h-44 text-sm text-gray-300">No trend data yet</div>
            ) : (
                <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={chartData} margin={{ top: 8, right: 4, left: -20, bottom: 0 }}>
                        <defs>
                            <linearGradient id="gradRisks" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%"   stopColor="#f43f5e" stopOpacity={0.25} />
                                <stop offset="100%" stopColor="#f43f5e" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="gradRemediated" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%"   stopColor="#38bdf8" stopOpacity={0.25} />
                                <stop offset="100%" stopColor="#38bdf8" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                        <XAxis
                            dataKey="date"
                            tick={<TrendXTick />}
                            axisLine={false}
                            tickLine={false}
                            interval={tickInterval}
                            height={36}
                        />
                        <YAxis
                            tick={{ fontSize: 10, fill: '#94a3b8' }}
                            axisLine={false}
                            tickLine={false}
                            allowDecimals={false}
                            width={50}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area
                            type="monotone"
                            dataKey="Risks"
                            name="Risks"
                            hide={!visible.Risks}
                            stroke="#f43f5e"
                            strokeWidth={2.5}
                            fill="url(#gradRisks)"
                            dot={showDots ? { r: 4, fill: '#f43f5e', strokeWidth: 2, stroke: '#fff' } : false}
                            activeDot={{ r: 5, fill: '#f43f5e', strokeWidth: 2, stroke: '#fff' }}
                        />
                        <Area
                            type="monotone"
                            dataKey="Remediated"
                            name="Remediated"
                            hide={!visible.Remediated}
                            stroke="#38bdf8"
                            strokeWidth={2.5}
                            fill="url(#gradRemediated)"
                            dot={showDots ? { r: 4, fill: '#38bdf8', strokeWidth: 2, stroke: '#fff' } : false}
                            activeDot={{ r: 5, fill: '#38bdf8', strokeWidth: 2, stroke: '#fff' }}
                        />
                    </AreaChart>
                </ResponsiveContainer>
            )}
        </Card>
    );
};

// Chart 1: Cumulative Overall Risk Score (donut) — kept as backup
const CumulativeOverallRiskScoreCard = ({ data }) => {
    const { overall_risk_score = 0, overall_RiskLevel = 'LOW', total_risks = 0 } = data || {};
    const hasAssessment = total_risks > 0;

    const getStrokeColor = (level) => {
        switch (level) {
            case 'CRITICAL': return '#b91c1c';
            case 'HIGH': return '#ea580c';
            case 'MEDIUM': return '#ca8a04';
            case 'LOW': return '#16a34a';
            default: return '#9ca3af';
        }
    };

    const strokeColor = getStrokeColor(overall_RiskLevel);
    return (
        <Card className="flex flex-col items-center relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(90deg,#0ea5e9,#8b5cf6)' }} />
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3 mt-2 self-start">Cumulative Risk Score</div>

            <div className="relative inline-flex items-center justify-center mb-3">
                <svg width="160" height="160" viewBox="0 0 160 160" style={{ transform: 'rotate(-90deg)' }}>
                    <circle cx="80" cy="80" r="68" stroke="#e2e8f0" strokeWidth="13" fill="none" />
                    <circle
                        cx="80" cy="80" r="68"
                        stroke={strokeColor}
                        strokeWidth="13" fill="none"
                        strokeLinecap="round"
                        strokeDasharray={`${(overall_risk_score / 100) * 427.257} 427.257`}
                        style={{ transition: 'stroke-dasharray 1.5s cubic-bezier(.4,0,.2,1)' }}
                    />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <div className={`text-3xl font-extrabold tracking-tight leading-none ${getScoreTextColor(overall_RiskLevel)}`}>{hasAssessment ? `${overall_risk_score}%` : '—'}</div>
                    <div className="text-xs font-semibold text-gray-400 tracking-wider mt-1">RISK SCORE</div>
                </div>
            </div>

            {hasAssessment ? (
                <>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border mb-2 ${getRiskBadgeClass(overall_RiskLevel)}`}>
                        {overall_RiskLevel}
                    </span>
                    <div className="text-xs text-gray-400">{total_risks} Active Risks · All Modules</div>
                </>
            ) : (
                <div className="text-xs text-gray-400 italic">Assessment Not Started</div>
            )}
        </Card>
    );
};

// Chart 2: Per-module risk status (progress bars)
// Fusion heatmap columns (F4): every module column can hold connected chains. The API names its
// buckets exactly as moduleStatus names the columns.
const FUSION_BUCKETS = ['Ent', 'Business Unit', 'Deal', 'Ransomware', 'Dark Web', 'AI Risk', 'TPRM', 'GRC', 'External'];

const RiskHeatmapCard = ({ moduleStatus, loading, fusionSummary, onFusionCell }) => {
    const LEVELS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'];
    const MODULE_ABBR = {
        'Ent': 'Ent', 'Ransomware': 'Ran', 'Dark Web': 'DW',
        'AI Risk': 'AI', 'Business Unit': 'BU', 'Deal': 'Deal', 'TPRM': 'TPRM', 'GRC': 'GRC',
        'External': 'Ext',
    };

    const getCell = (level, count, maxCount) => {
        if (count === 0) return { bg: 'bg-gray-50', text: 'text-gray-300' };
        const p = count / maxCount;
        const palettes = {
            CRITICAL: [['bg-red-100','text-red-600'],['bg-red-200','text-red-700'],['bg-red-400','text-white'],['bg-red-600','text-white']],
            HIGH:     [['bg-orange-100','text-orange-600'],['bg-orange-200','text-orange-700'],['bg-orange-400','text-white'],['bg-orange-500','text-white']],
            MEDIUM:   [['bg-yellow-100','text-yellow-700'],['bg-yellow-200','text-yellow-800'],['bg-yellow-400','text-yellow-900'],['bg-yellow-500','text-white']],
            LOW:      [['bg-green-100','text-green-600'],['bg-green-200','text-green-700'],['bg-green-400','text-white'],['bg-green-600','text-white']],
        };
        const idx = p > 0.75 ? 3 : p > 0.5 ? 2 : p > 0.25 ? 1 : 0;
        const [bg, text] = palettes[level][idx];
        return { bg, text };
    };

    if (loading || !moduleStatus || moduleStatus.length === 0) {
        return (
            <Card className="relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(90deg,#0ea5e9,#8b5cf6)' }} />
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3 mt-2">Risk Heatmap</div>
                <div className="text-sm text-gray-400 py-6 text-center">{loading ? 'Loading…' : 'No data available'}</div>
            </Card>
        );
    }

    const maxPerLevel = {};
    LEVELS.forEach(l => {
        maxPerLevel[l] = Math.max(...moduleStatus.map(m => m.riskCounts?.[l] || 0), 1);
    });

    const totalPerModule = moduleStatus.map(m =>
        LEVELS.reduce((s, l) => s + (m.riskCounts?.[l] || 0), 0)
    );

    return (
        <Card className="relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(90deg,#0ea5e9,#8b5cf6)' }} />
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3 mt-2">Risk Heatmap</div>

            <div className="overflow-x-auto">
                <table className="w-full text-center text-[10px]" style={{ borderCollapse: 'separate', borderSpacing: '2px' }}>
                    <thead>
                        <tr>
                            <th className="w-8"></th>
                            {moduleStatus.map(m => (
                                <th key={m.name} className="font-semibold text-gray-400 pb-1 px-0.5" title={m.name}>
                                    {MODULE_ABBR[m.name] || m.name.slice(0, 4)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {LEVELS.map(level => (
                            <tr key={level}>
                                <td className={`font-bold text-right pr-1.5 text-[9px] ${
                                    level === 'CRITICAL' ? 'text-red-500' :
                                    level === 'HIGH'     ? 'text-orange-500' :
                                    level === 'MEDIUM'   ? 'text-yellow-600' : 'text-green-600'
                                }`}>
                                    {level === 'CRITICAL' ? 'CRIT' : level === 'MEDIUM' ? 'MED' : level}
                                </td>
                                {moduleStatus.map(m => {
                                    const count = m.riskCounts?.[level] || 0;
                                    const { bg, text } = getCell(level, count, maxPerLevel[level]);
                                    // A cell opens Fusion only when the summary reports chains for it.
                                    const bucket = FUSION_BUCKETS.includes(m.name) ? m.name : null;
                                    const chains = fusionSummary && bucket ? fusionCellCount(fusionSummary, bucket, level) : 0;
                                    const base = `${m.name} — ${level}: ${count}`;
                                    const title = !fusionSummary ? base
                                        : chains > 0
                                            ? `${base}. ${chains} connected chain${chains === 1 ? '' : 's'} — click to open in Fusion.`
                                            : `${base}. Not connected in Fusion yet.`;
                                    return (
                                        <td key={m.name}
                                            className={`rounded ${bg} ${text} font-semibold py-1.5 ${chains > 0 ? 'cursor-pointer hover:brightness-95 hover:scale-105 transition-transform' : ''}`}
                                            title={title}
                                            onClick={chains > 0 ? () => onFusionCell?.(bucket, level) : undefined}
                                            role={chains > 0 ? 'button' : undefined}
                                        >
                                            {count > 0 ? count : '—'}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        {/* Total row */}
                        <tr className="border-t border-gray-100">
                            <td className="text-[9px] text-gray-400 font-semibold text-right pr-1.5 pt-1">TOT</td>
                            {totalPerModule.map((t, i) => (
                                <td key={i} className={`text-[10px] font-bold pt-1 ${t > 0 ? 'text-gray-700' : 'text-gray-300'}`}>{t || '—'}</td>
                            ))}
                        </tr>
                    </tbody>
                </table>
            </div>

            {/* Legend */}
            <div className="flex items-center gap-2 mt-3 pt-2 border-t border-gray-100 flex-wrap">
                {LEVELS.map(l => (
                    <div key={l} className="flex items-center gap-1">
                        <div className={`w-2 h-2 rounded-sm ${l === 'CRITICAL' ? 'bg-red-400' : l === 'HIGH' ? 'bg-orange-400' : l === 'MEDIUM' ? 'bg-yellow-400' : 'bg-green-400'}`} />
                        <span className="text-[9px] text-gray-400">{l === 'CRITICAL' ? 'Crit' : l === 'MEDIUM' ? 'Med' : l.charAt(0) + l.slice(1).toLowerCase()}</span>
                    </div>
                ))}
                <span className="text-[9px] text-gray-300 ml-auto">{fusionSummary ? 'click a cell to open it in Fusion' : 'darker = more'}</span>
            </div>
        </Card>
    );
};

// Organization Assessment Card - Enterprise level assessment for the organization
const OrganizationAssessmentCard = () => {
    const [orgAssessment, setOrgAssessment] = useState(null);
    const [loading, setLoading] = useState(true);

    const apiCall = useApi();
    const navigate = useNavigate();

    // Calculate risk score from counts
    const calculateRiskScore = (riskCounts = {}) => severityPostureScore(riskCounts);

    const getRiskLevelFromScore = (score) => severityPostureLevel(score);

    // Fetch assessment for the current organization.
    // `silent` skips the loading toggle so the live background refresh swaps numbers in place.
    const fetchOrgAssessment = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const org = JSON.parse(localStorage.getItem("selectedOrganization"));
            if (!org?.organizationId) return;

            // Pre-aggregated on the server (severity counts only) so we don't download every risk.
            const data = await apiCall("GET", `/enterpriseOrganizations/assessment-summary`);

            if (data?.hasAssessment) {
                const riskCounts = {
                    CRITICAL: data.criticalCount || 0,
                    HIGH: data.highCount || 0,
                    MEDIUM: data.mediumCount || 0,
                    LOW: data.lowCount || 0,
                };

                setOrgAssessment({
                    organizationName: data.organizationName,
                    industry: data.industry,
                    regionName: data.regionName,
                    latestAssessment: { enterpriseAssessmentID: data.latestAssessmentId },
                    riskCounts,
                    totalRisks: data.totalRisks || 0,
                    riskScore: calculateRiskScore(riskCounts),
                    hasAssessment: true,
                });
            } else {
                setOrgAssessment({
                    ...data,
                    hasAssessment: false,
                });
            }
        } catch (err) {
            setOrgAssessment(null);
        } finally {
            if (!silent) setLoading(false);
        }
    }, [apiCall]);

    // Fetch organization assessment on mount, then keep it live.
    useEffect(() => { fetchOrgAssessment(false); }, [fetchOrgAssessment]);
    const liveRefreshOrgAssessment = useCallback(() => fetchOrgAssessment(true), [fetchOrgAssessment]);
    useLiveDashboard({ refresh: liveRefreshOrgAssessment });

    // Don't render if no assessment exists
    if (!loading && (!orgAssessment || !orgAssessment.hasAssessment)) {
        return null;
    }

    const score = orgAssessment?.riskScore || 0;
    const level = getRiskLevelFromScore(score);
    const totalRisks = orgAssessment?.totalRisks || 0;
    const riskCounts = orgAssessment?.riskCounts || {};

    return (
        <Card className="relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(90deg,#0ea5e9,#8b5cf6)' }} />
            <div className="flex items-center justify-between mb-5 mt-1">
                <SectionTitle>Organization Assessment</SectionTitle>
                <button
                    onClick={() => navigate("/EnterpriseOrganizations")}
                    className="text-xs text-sky-600 hover:underline font-semibold"
                >
                    View All →
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500" />
                </div>
            ) : (
                <div className="flex items-center gap-6 flex-wrap">
                    {/* Score box */}
                    <div className="flex flex-col items-center justify-center bg-gray-50 border border-gray-200 rounded-2xl px-8 py-6 flex-shrink-0 min-w-[130px]">
                        <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">RISK SCORE</div>
                        <div className={`text-4xl font-extrabold tracking-tight leading-none ${getScoreTextColor(level)}`}>{score}%</div>
                        <span className={`mt-2 px-2 py-0.5 rounded-full text-xs font-bold border ${getRiskBadgeClass(level)}`}>{level}</span>
                        <div className="text-xs text-gray-400 mt-1">{totalRisks} total risks</div>
                    </div>

                    {/* Details + distribution — flex-1 */}
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-gray-700 mb-1">
                            {orgAssessment.organizationName}
                            {orgAssessment.industry && <span className="font-normal text-gray-400"> &nbsp;·&nbsp; {orgAssessment.industry}</span>}
                            {orgAssessment.regionName && <span className="font-normal text-gray-400"> &nbsp;·&nbsp; {orgAssessment.regionName}</span>}
                        </div>

                        {/* Risk distribution boxes */}
                        <div className="flex gap-2 mb-3">
                            {[
                                { key: 'CRITICAL', label: 'CRITICAL', cls: 'text-red-600' },
                                { key: 'HIGH',     label: 'HIGH',     cls: 'text-orange-600' },
                                { key: 'MEDIUM',   label: 'MEDIUM',   cls: 'text-yellow-600' },
                                { key: 'LOW',      label: 'LOW',      cls: 'text-green-600' },
                            ].map(({ key, label, cls }) => (
                                <div key={key} className="flex-1 text-center bg-[#f1f5f9] border border-[#e2e8f0] rounded-[10px] px-2 py-2.5">
                                    <div className={`text-lg font-extrabold leading-none ${cls}`}>{riskCounts[key] || 0}</div>
                                    <div className="text-[9px] font-semibold text-gray-400 tracking-wide mt-0.5">{label}</div>
                                </div>
                            ))}
                        </div>

                        {/* Risk bar */}
                        <div>
                            <div className="flex justify-between text-xs mb-1">
                                <span className="text-gray-500">Risk Level</span>
                                <span className={`font-bold ${getScoreTextColor(level)}`}>{level} ({score}%)</span>
                            </div>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden border border-gray-100">
                                <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${score}%`, background: 'linear-gradient(90deg,#0ea5e9,#8b5cf6)' }} />
                            </div>
                        </div>
                    </div>

                    {/* Action button — pushed to far right */}
                    <div className="ml-auto flex items-center flex-shrink-0">
                        {orgAssessment.latestAssessment?.enterpriseAssessmentID ? (
                            <button
                                onClick={() => navigate(`/EnterpriseAssessments/${orgAssessment.latestAssessment.enterpriseAssessmentID}/risks`)}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-px"
                                style={{ background: '#0ea5e9', boxShadow: '0 2px 8px rgba(14,165,233,.3)' }}
                                onMouseEnter={e => { e.currentTarget.style.background = '#0284c7'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(14,165,233,.4)'; }}
                                onMouseLeave={e => { e.currentTarget.style.background = '#0ea5e9'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(14,165,233,.3)'; }}
                            >
                                🛡 View {totalRisks} Risks
                            </button>
                        ) : (
                            <span className="px-4 py-2 rounded-xl text-sm font-semibold bg-gray-100 text-gray-400 border border-gray-200">No Risks</span>
                        )}
                    </div>
                </div>
            )}
        </Card>
    );
};



// Recent Deals Card - 3 Application deals + 3 Business Unit deals in two columns
const RecentDealsCard = () => {
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [applications, setApplications] = useState([]);
    const [applicationsWithRisks, setApplicationsWithRisks] = useState([]);
    const [selectedDeal, setSelectedDeal] = useState(null);
    const [loadingApplications, setLoadingApplications] = useState(false);

    const [businessUnits, setBusinessUnits] = useState([]);
    const [loadingBUs, setLoadingBUs] = useState(true);
    const [selectedBU, setSelectedBU] = useState(null);
    const [buApplications, setBuApplications] = useState([]);
    const [loadingBUApplications, setLoadingBUApplications] = useState(false);

    const apiCall = useApi();
    const navigate = useNavigate();

    // ----------------- Calculate Risk -----------------
    const calculateRiskScore = (riskCounts = {}) => severityPostureScore(riskCounts);

    const getRiskLevelFromScore = (score) => severityPostureLevel(score);

    const getRiskPercentage = (riskLevel) => {
        switch (riskLevel) {
            case "CRITICAL": return 90;
            case "HIGH": return 75;
            case "MEDIUM": return 50;
            case "LOW": return 25;
            default: return 10;
        }
    };

    // ----------------- Fetch Business Units -----------------
    // `silent` skips the loading toggle so the live background refresh updates in place.
    const fetchBusinessUnits = useCallback(async (silent = false) => {
        try {
            const data = await apiCall("GET", `/businessunits/summary?page=1&pageSize=10`);
            const items = (data.items || []).map((bu) => ({
                businessUnitId: bu.businessUnitId,
                businessUnitName: bu.businessUnitName,
                domain: bu.domain,
                applicationCount: bu.applicationCount,
                riskLevel: bu.riskLevel === "NONE" ? null : bu.riskLevel,
                riskCounts: {
                    CRITICAL: bu.riskCounts?.critical || 0,
                    HIGH: bu.riskCounts?.high || 0,
                    MEDIUM: bu.riskCounts?.medium || 0,
                    LOW: bu.riskCounts?.low || 0,
                },
            }));
            const sorted = items.sort((a, b) => {
                const aTotal = Object.values(a.riskCounts).reduce((s, v) => s + v, 0);
                const bTotal = Object.values(b.riskCounts).reduce((s, v) => s + v, 0);
                if ((aTotal > 0) !== (bTotal > 0)) return aTotal > 0 ? -1 : 1;
                return (a.businessUnitName || '').localeCompare(b.businessUnitName || '');
            });
            setBusinessUnits(sorted.slice(0, 3));
        } catch (err) {} finally {
            if (!silent) setLoadingBUs(false);
        }
    }, [apiCall]);

    const fetchBUApplications = async (businessUnitId) => {
        setLoadingBUApplications(true);
        setBuApplications([]);
        try {
            const summaries = await apiCall("GET", `/businessunitassessments/summary?businessUnitId=${businessUnitId}`);
            setBuApplications(summaries || []);
        } catch {
            setBuApplications([]);
        } finally {
            setLoadingBUApplications(false);
        }
    };

    // ----------------- Fetch Deals -----------------
    // `silent` skips the loading toggle so the live background refresh updates in place.
    const fetchDeals = useCallback(async (silent = false) => {
        try {
            // Fetch deal details + summary risk data in parallel
            const [recentData, summaryData] = await Promise.all([
                apiCall("GET", `/deals/recent`),
                apiCall("GET", `/deals/summary?page=1&pageSize=50`),
            ]);

            // Build a lookup from dealId → risk counts from the summary endpoint
            const summaryMap = {};
            for (const s of (summaryData?.items || [])) {
                const riskCounts = {
                    CRITICAL: s.riskCounts?.critical ?? 0,
                    HIGH:     s.riskCounts?.high     ?? 0,
                    MEDIUM:   s.riskCounts?.medium   ?? 0,
                    LOW:      s.riskCounts?.low      ?? 0,
                };
                summaryMap[s.dealId] = {
                    riskCounts,
                    riskScore: calculateRiskScore(riskCounts),
                };
            }

            // Merge recent deal details with summary risk data
            const merged = (recentData || []).map((deal) => {
                const summary = summaryMap[deal.dealID] || {};
                const riskCounts = summary.riskCounts || { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 };
                return {
                    ...deal,
                    riskCounts,
                    riskScore: summary.riskScore || 0,
                };
            });

            // Sort: assessed first → higher risk score → most recent date, then take top 3
            const sorted = merged.sort((a, b) => {
                if (a.hasAssessment !== b.hasAssessment) return a.hasAssessment ? -1 : 1;
                if (b.riskScore !== a.riskScore) return b.riskScore - a.riskScore;
                return new Date(b.dealStartDate) - new Date(a.dealStartDate);
            });
            setDeals(sorted.slice(0, 3));
        } catch (err) {} finally {
            if (!silent) setLoading(false);
        }
    }, [apiCall]);

    // Refresh deals + business units together, then keep them live.
    const refreshData = useCallback(async (silent = false) => {
        const org = JSON.parse(localStorage.getItem("selectedOrganization"));
        if (org?.organizationId) {
            await Promise.all([fetchDeals(silent), fetchBusinessUnits(silent)]);
        } else if (!silent) {
            setLoading(false);
            setLoadingBUs(false);
        }
    }, [fetchDeals, fetchBusinessUnits]);

    useEffect(() => { refreshData(false); }, [refreshData]);
    const liveRefreshDeals = useCallback(() => refreshData(true), [refreshData]);
    useLiveDashboard({ refresh: liveRefreshDeals });

    const fetchApplications = async (dealID) => {
        setLoadingApplications(true);
        setApplications([]);
        setApplicationsWithRisks([]);

        try {
            const data = await apiCall("GET", `/deals/${dealID}/applications`);
            setApplications(data || []);

            const appsWithRisks = await Promise.all(
                (data || []).map(async (app) => {
                    try {
                        const assessments = await apiCall("GET", `/assessments?applicationId=${app.applicationId}`);


                        if (!assessments || assessments.length === 0) {
                            return {
                                ...app,
                                assessmentId: null,
                                risks: [],
                                riskScore: 0,
                                hasAssessment: false,
                                riskCounts: { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 },
                                totalRisks: 0
                            };
                        }

                        const latest = assessments[0];
                        const risks = await apiCall("GET", `/assessments/${latest.assessmentID}/risks`);

                        const normalizeRating = (rating) => (rating || "").toString().toUpperCase();

                        const riskCounts = {
                            CRITICAL: risks.filter(r => normalizeRating(r.userRating || r.residualRiskRating || r.inherentRiskRating) === "CRITICAL").length,
                            HIGH: risks.filter(r => normalizeRating(r.userRating || r.residualRiskRating || r.inherentRiskRating) === "HIGH").length,
                            MEDIUM: risks.filter(r => normalizeRating(r.userRating || r.residualRiskRating || r.inherentRiskRating) === "MEDIUM").length,
                            LOW: risks.filter(r => normalizeRating(r.userRating || r.residualRiskRating || r.inherentRiskRating) === "LOW").length,
                        };

                        const totalRisks = risks.length;
                        const riskScore = latest.overall_risk_score || calculateRiskScore(riskCounts);

                        return { ...app, assessmentId: latest.assessmentID, risks, riskCounts, totalRisks, riskScore, hasAssessment: true };
                    } catch {
                        return { ...app, assessmentId: null, risks: [], riskCounts: { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0 }, totalRisks: 0, riskScore: 0, hasAssessment: false };
                    }
                })
            );

            setApplicationsWithRisks(appsWithRisks);
        } catch (error) {} finally {
            setLoadingApplications(false);
        }
    };

    // ----------------- Render -----------------
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* ------------------ LEFT: APPLICATION DEALS ------------------ */}
            {(loading || deals.length > 0) && <Card>
                <div className="flex justify-between items-center mb-4">
                    <SectionTitle>Recent Deals</SectionTitle>
                    <button onClick={() => navigate("/deals")} className="text-xs text-sky-600 hover:underline font-semibold">View All Deals →</button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {deals.map((deal) => {
                                const totalRisks = Object.values(deal.riskCounts || {}).reduce((a, b) => a + b, 0);
                                const hasRiskData = deal.hasAssessment && totalRisks > 0;
                                const riskLevel = hasRiskData ? getRiskLevelFromScore(deal.riskScore) : null;
                                const riskPct = hasRiskData ? getRiskPercentage(riskLevel) : 0;
                                const badgeClass = riskLevel === 'CRITICAL' ? 'bg-red-100 text-red-700' : riskLevel === 'HIGH' ? 'bg-orange-100 text-orange-600' : riskLevel === 'MEDIUM' ? 'bg-yellow-100 text-yellow-600' : riskLevel === 'LOW' ? 'bg-green-100 text-green-600' : '';
                                return (
                                <div
                                    key={deal.dealID}
                                    className="relative bg-white border border-gray-200 rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-300"
                                    onClick={() => {
                                        setSelectedDeal(deal);
                                        fetchApplications(deal.dealID);
                                    }}
                                >
                                    <div className={`h-1 ${hasRiskData ? getDealLeftBorderColor(riskLevel) : 'bg-gray-200'}`} />
                                    <div className="p-4">
                                        <div className="flex justify-between items-start gap-2 mb-1.5">
                                            <h4 className="font-bold text-gray-900 text-sm leading-snug truncate">{deal.dealName}</h4>
                                            {deal.isPrivate && (
                                                <span className="shrink-0 inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600"><Lock className="h-3 w-3" />Private</span>
                                            )}
                                            {hasRiskData && (
                                                <span className={`shrink-0 text-xs font-bold px-2 py-0.5 rounded-full ${badgeClass}`}>{riskLevel}</span>
                                            )}
                                        </div>
                                        {(() => { const city = deal.dealCity || deal.DealCity; const country = deal.dealCountry; const loc = [city, country].filter(Boolean).join(', '); return loc ? <p className="text-xs text-gray-400 mb-3">📍 {loc}</p> : null; })()}
                                        <div className="flex items-center gap-1.5 mb-3">
                                            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
                                                <span className="text-[10px] font-bold text-gray-500">{(deal.dealOwner || 'N')[0]?.toUpperCase()}</span>
                                            </div>
                                            <span className="text-xs text-gray-500 truncate">{deal.dealOwner || 'N/A'}</span>
                                        </div>
                                        {hasRiskData ? (
                                            <div className="mb-3">
                                                <div className="flex justify-between text-xs mb-1">
                                                    <span className="text-gray-400">Risk Score</span>
                                                    <span className={`font-bold ${getDealTextColor(riskLevel)}`}>{deal.riskScore}%</span>
                                                </div>
                                                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                    <div className={`h-full rounded-full ${getDealLeftBorderColor(riskLevel)}`} style={{ width: `${riskPct}%` }} />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-xs text-gray-300 italic mb-3">No assessment yet</div>
                                        )}
                                        {(() => { const fmt = (d) => { const dt = d ? new Date(d) : null; return dt && !isNaN(dt) && dt.getFullYear() > 2000 ? dt.toLocaleDateString() : null; }; const s = fmt(deal.dealStartDate); const e = fmt(deal.dealEndDate); return (s || e) ? <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-100"><span>{s || '—'}</span><span className="text-gray-300">→</span><span>{e || '—'}</span></div> : null; })()}
                                    </div>
                                </div>
                                );
                            })}
                        </div>

                        {selectedDeal && (
                            <div className="mt-6">
                                <h2 className="text-lg font-bold mb-2">Applications for {selectedDeal.dealName}</h2>
                                {loadingApplications ? (
                                    <div className="flex items-center justify-center py-4">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                    </div>
                                ) : applicationsWithRisks.length === 0 ? (
                                    <p className="text-gray-500">No applications found.</p>
                                ) : (
                                    <ul className="space-y-2">
                                        {applicationsWithRisks.map((app) => (
                                            <li key={app.applicationId} className="p-2 border rounded shadow-sm">
                                                <div className="font-medium">{app.applicationName}</div>
                                                <div className="text-sm text-gray-500 mb-1">Risk Score: {app.riskScore}%</div>
                                                <div className="w-full bg-gray-200 rounded-full h-4">
                                                    <div
                                                        className={`h-4 rounded-full ${app.riskScore <= 40
                                                            ? 'bg-green-500'
                                                            : app.riskScore <= 60
                                                                ? 'bg-yellow-500'
                                                                : app.riskScore <= 80
                                                                    ? 'bg-orange-500'
                                                                    : 'bg-red-500'
                                                            }`}
                                                        style={{ width: `${app.riskScore}%` }}
                                                    ></div>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </>
                )}
            </Card>}

            {/* ------------------ RIGHT: BUSINESS UNIT DEALS ------------------ */}
            {(loadingBUs || businessUnits.length > 0) && <Card>
                <div className="flex justify-between items-center mb-4">
                    <SectionTitle>Business Units</SectionTitle>
                    <button onClick={() => navigate("/businessunits")} className="text-xs text-sky-600 hover:underline font-semibold">View All →</button>
                </div>

                {loadingBUs ? (
                    <div className="flex items-center justify-center py-4">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            {businessUnits.map((bu) => {
                                const totalRisks = Object.values(bu.riskCounts || {}).reduce((a, b) => a + b, 0);
                                const hasRiskData = totalRisks > 0;
                                const riskScore = calculateRiskScore(bu.riskCounts);
                                const riskLevel = hasRiskData ? (bu.riskLevel || getRiskLevelFromScore(riskScore)) : null;
                                const riskPct = hasRiskData ? getRiskPercentage(riskLevel) : 0;
                                const badgeClass = riskLevel === 'CRITICAL' ? 'bg-red-100 text-red-700' : riskLevel === 'HIGH' ? 'bg-orange-100 text-orange-600' : riskLevel === 'MEDIUM' ? 'bg-yellow-100 text-yellow-600' : riskLevel === 'LOW' ? 'bg-green-100 text-green-600' : '';
                                return (
                                <div
                                    key={bu.businessUnitId}
                                    className="relative bg-white border border-gray-200 rounded-xl overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:border-gray-300"
                                    onClick={() => {
                                        setSelectedBU(bu);
                                        fetchBUApplications(bu.businessUnitId);
                                    }}
                                >
                                    <div className={`h-1 ${hasRiskData ? getDealLeftBorderColor(riskLevel) : 'bg-gray-200'}`} />
                                    <div className="p-4">
                                        <div className="flex justify-between items-start gap-2 mb-1.5">
                                            <h4 className="font-bold text-gray-900 text-sm leading-snug truncate">{bu.businessUnitName}</h4>
                                            {hasRiskData && (
                                                <span className={`shrink-0 text-xs font-bold px-2 py-0.5 rounded-full ${badgeClass}`}>{riskLevel}</span>
                                            )}
                                        </div>
                                        {bu.domain && <p className="text-xs text-gray-400 mb-3">🌐 {bu.domain}</p>}
                                        <div className="flex items-center gap-1.5 mb-3">
                                            <span className="text-xs text-gray-500">{bu.applicationCount || 0} application{bu.applicationCount !== 1 ? 's' : ''}</span>
                                        </div>
                                        {hasRiskData ? (
                                            <div className="mb-3">
                                                <div className="flex justify-between text-xs mb-1">
                                                    <span className="text-gray-400">Risk Score</span>
                                                    <span className={`font-bold ${getDealTextColor(riskLevel)}`}>{riskScore}%</span>
                                                </div>
                                                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                    <div className={`h-full rounded-full ${getDealLeftBorderColor(riskLevel)}`} style={{ width: `${riskPct}%` }} />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-xs text-gray-300 italic mb-3">No assessment yet</div>
                                        )}
                                    </div>
                                </div>
                                );
                            })}
                        </div>

                        {selectedBU && (
                            <div className="mt-6">
                                <h2 className="text-lg font-bold mb-2">Applications for {selectedBU.businessUnitName}</h2>
                                {loadingBUApplications ? (
                                    <div className="flex items-center justify-center py-4">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                    </div>
                                ) : buApplications.length === 0 ? (
                                    <p className="text-gray-500">No applications found.</p>
                                ) : (
                                    <ul className="space-y-2">
                                        {buApplications.map((app) => {
                                            const appRiskScore = calculateRiskScore({
                                                CRITICAL: app.riskCounts?.critical || 0,
                                                HIGH: app.riskCounts?.high || 0,
                                                MEDIUM: app.riskCounts?.medium || 0,
                                                LOW: app.riskCounts?.low || 0,
                                            });
                                            return (
                                                <li key={app.businessUnitApplicationId} className="p-2 border rounded shadow-sm">
                                                    <div className="font-medium">{app.applicationName}</div>
                                                    <div className="text-sm text-gray-500 mb-1">Risk Score: {appRiskScore}%</div>
                                                    <div className="w-full bg-gray-200 rounded-full h-4">
                                                        <div
                                                            className={`h-4 rounded-full ${appRiskScore <= 40
                                                                ? 'bg-green-500'
                                                                : appRiskScore <= 60
                                                                    ? 'bg-yellow-500'
                                                                    : appRiskScore <= 80
                                                                        ? 'bg-orange-500'
                                                                        : 'bg-red-500'
                                                                }`}
                                                            style={{ width: `${appRiskScore}%` }}
                                                        ></div>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        )}
                    </>
                )}
            </Card>}

        </div>
    );
};


// Remediation Donut Card — compact donut + legend (used in top 4-card row)
const RemediationDonutCard = () => {
    const apiCall = useApi();
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    // `silent` skips the loading toggle so the live background refresh updates in place.
    const fetchSummary = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const org = JSON.parse(localStorage.getItem("selectedOrganization"));
            if (!org?.organizationId) return;
            // Pre-aggregated on the server (counts only) so we don't download every remediation.
            const data = await apiCall('GET', '/allRemediations/summary');
            setSummary({
                completedCount:  data?.completedCount  || 0,
                overdueCount:    data?.overdueCount    || 0,
                inProgressCount: data?.inProgressCount || 0,
                notStartedCount: data?.notStartedCount || 0,
            });
        } catch {
            setSummary({ completedCount: 0, inProgressCount: 0, overdueCount: 0, notStartedCount: 0 });
        } finally {
            if (!silent) setLoading(false);
        }
    }, [apiCall]);

    useEffect(() => { fetchSummary(false); }, [fetchSummary]);
    const liveRefreshDonut = useCallback(() => fetchSummary(true), [fetchSummary]);
    useLiveDashboard({ refresh: liveRefreshDonut });

    if (loading) {
        return (
            <Card className="relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(90deg,#0ea5e9,#8b5cf6)' }} />
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3 mt-2">Remediation Status</div>
                <div className="text-sm text-gray-400 py-6 text-center">Loading…</div>
            </Card>
        );
    }

    const rawSegments = [
        { color: '#16a34a', darkColor: '#0d5c2e', label: 'Completed',   val: summary.completedCount  || 0, cls: 'text-green-600' },
        { color: '#2563eb', darkColor: '#13409e', label: 'In Progress',  val: summary.inProgressCount || 0, cls: 'text-blue-600'  },
        { color: '#dc2626', darkColor: '#8c1818', label: 'Overdue',      val: summary.overdueCount    || 0, cls: 'text-red-600'   },
        { color: '#f97316', darkColor: '#c2540a', label: 'Not Started',  val: summary.notStartedCount || 0, cls: 'text-orange-500' },
    ];

    const total = rawSegments.reduce((s, seg) => s + seg.val, 0);

    // 3-D pie — ~60 % larger than previous; no inline labels (legend shown above)
    const cx = 180, cy = 70, rx = 170, ry = 62, depth = 22;
    const pt = (angle, dy = 0) => ({
        x: +(cx + rx * Math.cos(angle)).toFixed(2),
        y: +(cy + ry * Math.sin(angle) + dy).toFixed(2),
    });

    let cumPct = 0;
    const segs = total > 0
        ? rawSegments.filter(s => s.val > 0).map((s, idx) => {
            const startPct = cumPct;
            cumPct += s.val / total;
            return {
                ...s,
                idx,
                startAngle: -Math.PI / 2 + startPct * 2 * Math.PI,
                endAngle:   -Math.PI / 2 + cumPct  * 2 * Math.PI,
            };
        })
        : [];

    const topPath = (sa, ea) => {
        if (ea - sa >= 2 * Math.PI - 0.001)
            return `M ${cx - rx} ${cy} A ${rx} ${ry} 0 1 1 ${cx + rx} ${cy} A ${rx} ${ry} 0 1 1 ${cx - rx} ${cy} Z`;
        const s = pt(sa), e = pt(ea);
        return `M ${cx} ${cy} L ${s.x} ${s.y} A ${rx} ${ry} 0 ${(ea - sa) > Math.PI ? 1 : 0} 1 ${e.x} ${e.y} Z`;
    };

    const sidePath = (sa, ea) => {
        const csa = Math.max(sa, 0), cea = Math.min(ea, Math.PI);
        if (csa >= cea) return null;
        const s = pt(csa), e = pt(cea), sd = pt(csa, depth), ed = pt(cea, depth);
        return `M ${s.x} ${s.y} A ${rx} ${ry} 0 ${(cea - csa) > Math.PI ? 1 : 0} 1 ${e.x} ${e.y} L ${ed.x} ${ed.y} A ${rx} ${ry} 0 ${(cea - csa) > Math.PI ? 1 : 0} 0 ${sd.x} ${sd.y} Z`;
    };

    return (
        <Card className="relative flex flex-col overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-0.5 rounded-t-2xl" style={{ background: 'linear-gradient(90deg,#0ea5e9,#8b5cf6)' }} />
            <div className="flex items-center justify-between mt-2 px-3">
                <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Remediation Status</div>
                <button onClick={() => navigate('/remediations')} className="text-xs text-sky-500 hover:text-sky-700 font-semibold hover:underline transition-colors">View All →</button>
            </div>

            {/* Legend — vertically centred between heading and pie */}
            <div className="flex-1 flex items-center justify-center">
                <div className="grid grid-cols-2 gap-x-8 gap-y-3 px-3">
                    {rawSegments.map(seg => (
                        <div key={seg.label} className="flex items-center gap-2 min-w-0">
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: seg.color }} />
                            <span className="text-xs text-gray-500 truncate">{seg.label}</span>
                            <span className="text-xs font-bold ml-auto flex-shrink-0" style={{ color: seg.color }}>{seg.val}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Pie chart — pushed toward the bottom of the card */}
            <div className="flex items-end justify-center pb-3">
                <svg width="100%" viewBox="0 0 360 165">
                    <defs>
                        <filter id="pie3dShadow" x="-5%" y="-10%" width="110%" height="130%">
                            <feDropShadow dx="0" dy="4" stdDeviation="5" floodColor="#00000028" />
                        </filter>
                    </defs>

                    {/* 3-D pie body */}
                    <g filter="url(#pie3dShadow)">
                        {total === 0 ? (
                            <>
                                <ellipse cx={cx} cy={cy + depth} rx={rx} ry={ry} fill="#cbd5e1" />
                                <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="#e2e8f0" stroke="white" strokeWidth="1.5" />
                            </>
                        ) : (
                            <>
                                {segs.map(s => { const d = sidePath(s.startAngle, s.endAngle); return d ? <path key={`sw-${s.label}`} d={d} fill={s.darkColor} /> : null; })}
                                {segs.map(s => <path key={`tp-${s.label}`} d={topPath(s.startAngle, s.endAngle)} fill={s.color} stroke="white" strokeWidth="1.5" />)}
                            </>
                        )}
                    </g>
                </svg>
            </div>
        </Card>
    );
};


// Chart 3: Cumulative vulnerability distribution (combined from all modules)

const CumulativeVulnerabilityDistributionCard = ({ data }) => {
    const { risk_distribution = {}, total_risks = 0 } = data || {};
    const criticalCount = risk_distribution.CRITICAL || 0;
    const highCount = risk_distribution.HIGH || 0;
    const mediumCount = risk_distribution.MEDIUM || 0;
    const lowCount = risk_distribution.LOW || 0;

    return (
        <Card className="relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(90deg,#0ea5e9,#8b5cf6)' }} />
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3 mt-2">Vulnerability Distribution</div>

            {/* Stacked proportional bar */}
            {total_risks > 0 && (
                <div className="flex h-5 rounded-lg overflow-hidden mb-4 bg-gray-100 border border-gray-200">
                    {criticalCount > 0 && <div className="bg-red-600" style={{ width: `${(criticalCount / total_risks) * 100}%` }} title={`Critical: ${criticalCount}`} />}
                    {highCount > 0 && <div className="bg-orange-500" style={{ width: `${(highCount / total_risks) * 100}%` }} title={`High: ${highCount}`} />}
                    {mediumCount > 0 && <div className="bg-yellow-500" style={{ width: `${(mediumCount / total_risks) * 100}%` }} title={`Medium: ${mediumCount}`} />}
                    {lowCount > 0 && <div className="bg-green-500" style={{ width: `${(lowCount / total_risks) * 100}%` }} title={`Low: ${lowCount}`} />}
                </div>
            )}

            {/* 2-column grid */}
            <div className="grid grid-cols-2 gap-x-3 gap-y-2 mb-4">
                <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-red-700" /><span className="text-gray-600">Critical</span></div>
                    <span className="font-bold text-red-700">{criticalCount}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-orange-500" /><span className="text-gray-600">High</span></div>
                    <span className="font-bold text-orange-500">{highCount}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-yellow-500" /><span className="text-gray-600">Medium</span></div>
                    <span className="font-bold text-yellow-600">{mediumCount}</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-1.5"><div className="w-2 h-2 rounded-full bg-green-500" /><span className="text-gray-600">Low</span></div>
                    <span className="font-bold text-green-600">{lowCount}</span>
                </div>
            </div>

            <div className="border-t border-gray-100 pt-3 text-center">
                <div className="text-xl font-extrabold text-gray-900 tracking-tight">{total_risks}</div>
                <div className="text-xs text-gray-400">Active Risks · All Modules</div>
            </div>
        </Card>
    );
};


// Chart 4: Cumulative predictive risk timeline (45/90/180 days)
const CumulativePredictiveRiskBars = ({ data, loading, modules = [], cumulativeData }) => {
    if (loading) {
        return (
            <Card className="text-center">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-4">
                    Predictive Risk Timeline
                </h3>
                <div className="text-sm text-gray-500 py-6">Loading...</div>
            </Card>
        );
    }

    const { day45 = 0, day90 = 0, day180 = 0 } = data || {};
    const hasAssessment = (cumulativeData?.total_risks || 0) > 0;

    return (
        <Card className="relative overflow-hidden flex flex-col">
            <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(90deg,#0ea5e9,#8b5cf6)' }} />
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3 mt-2">Predictive Risk Timeline</div>

            <div className="flex-1 space-y-4">
                <div>
                    <div className="flex justify-between items-center text-xs mb-1.5">
                        <span className="font-semibold text-gray-600">45 Days</span>
                        <span className="font-bold text-yellow-700">{hasAssessment ? `${day45}%` : '—'}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden border border-gray-100">
                        <div className="h-full rounded-full transition-all duration-1000" style={{ width: hasAssessment ? `${day45}%` : '0%', background: 'linear-gradient(90deg,#ca8a04,#d97706)' }} />
                    </div>
                    {hasAssessment && <div className="text-xs text-gray-400 mt-1">↗ Moderate escalation expected</div>}
                </div>
                <div>
                    <div className="flex justify-between items-center text-xs mb-1.5">
                        <span className="font-semibold text-gray-600">90 Days</span>
                        <span className="font-bold text-orange-700">{hasAssessment ? `${day90}%` : '—'}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden border border-gray-100">
                        <div className="h-full rounded-full transition-all duration-1000" style={{ width: hasAssessment ? `${day90}%` : '0%', background: 'linear-gradient(90deg,#ea580c,#f97316)' }} />
                    </div>
                    {hasAssessment && <div className="text-xs text-gray-400 mt-1">↗ High risk threshold approaching</div>}
                </div>
                <div>
                    <div className="flex justify-between items-center text-xs mb-1.5">
                        <span className="font-semibold text-gray-600">180 Days</span>
                        <span className="font-bold text-red-700">{hasAssessment ? `${day180}%` : '—'}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden border border-gray-100">
                        <div className="h-full rounded-full transition-all duration-1000" style={{ width: hasAssessment ? `${day180}%` : '0%', background: 'linear-gradient(90deg,#dc2626,#ef4444)' }} />
                    </div>
                    {hasAssessment && <div className="text-xs text-gray-400 mt-1">⚠ Critical risk without intervention</div>}
                </div>
            </div>

            <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-400 text-center">
                {hasAssessment
                    ? (modules.length > 0 ? `Risk-weighted · ${modules.join(' · ')}` : 'Risk-weighted forecast · All Modules')
                    : <span className="italic">Assessment Not Started</span>}
            </div>
        </Card>
    );
};
// Main App Component
// RemediationStatusTracker Component for Dashboard
const RemediationStatusTracker = () => {
    const { user } = useAuth();
    const apiCall = useApi();  // Fix: useApi returns the function directly, not an object
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);

    // `silent` skips the loading toggle so the live background refresh updates in place.
    const fetchRemediationSummary = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const org = JSON.parse(localStorage.getItem("selectedOrganization"));
            if (!org?.organizationId) {
                return;
            }

            const response = await apiCall(
                'GET',
                `/remediations/summary`
            );
            //console.log("👉 Remediation summary for org", org.organizationId, response);
            setSummary(response);
        } catch (error) {
            setSummary({
                totalRemediations: 0,
                byStatus: [],
                byPriority: [],
                overdueCount: 0,
                notStartedCount: 0,
                inProgressCount: 0,
                openCount: 0,
                completedCount: 0,
                top_in_progress: []
            });
        } finally {
            if (!silent) setLoading(false);
        }
    }, [apiCall]);

    useEffect(() => { fetchRemediationSummary(false); }, [fetchRemediationSummary]);
    const liveRefreshTracker = useCallback(() => fetchRemediationSummary(true), [fetchRemediationSummary]);
    useLiveDashboard({ refresh: liveRefreshTracker });


    if (loading) return <LoadingSpinner />;

    const total = (summary.completedCount || 0) + (summary.inProgressCount || 0) + (summary.overdueCount || 0) + (summary.notStartedCount || 0);
    const C = 2 * Math.PI * 56; // circumference for r=56
    const completedArc   = total > 0 ? (summary.completedCount   / total) * C : 0;
    const inProgressArc  = total > 0 ? (summary.inProgressCount  / total) * C : 0;
    const overdueArc     = total > 0 ? (summary.overdueCount     / total) * C : 0;
    const notStartedArc  = total > 0 ? (summary.notStartedCount  / total) * C : 0;

    return (
        <Card>
            <div className="flex items-center justify-between mb-5">
                <SectionTitle>Remediation Status Tracker</SectionTitle>
                <button onClick={() => navigate('/remediations?types=Application')} className="text-xs text-sky-600 hover:underline font-semibold">View All →</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start">
                {/* Left — segmented donut + legend */}
                <div className="flex flex-col items-center">
                    <div className="relative w-36 h-36">
                        <svg width="140" height="140" viewBox="0 0 140 140" style={{ transform: 'rotate(-90deg)' }}>
                            <circle cx="70" cy="70" r="56" stroke="#e2e8f0" strokeWidth="14" fill="none" />
                            {completedArc > 0 && (
                                <circle cx="70" cy="70" r="56" stroke="#16a34a" strokeWidth="14" fill="none"
                                    strokeDasharray={`${completedArc} ${C - completedArc}`} strokeDashoffset="0" strokeLinecap="round" />
                            )}
                            {inProgressArc > 0 && (
                                <circle cx="70" cy="70" r="56" stroke="#2563eb" strokeWidth="14" fill="none"
                                    strokeDasharray={`${inProgressArc} ${C - inProgressArc}`} strokeDashoffset={`-${completedArc}`} strokeLinecap="round" />
                            )}
                            {overdueArc > 0 && (
                                <circle cx="70" cy="70" r="56" stroke="#dc2626" strokeWidth="14" fill="none"
                                    strokeDasharray={`${overdueArc} ${C - overdueArc}`} strokeDashoffset={`-${completedArc + inProgressArc}`} strokeLinecap="round" />
                            )}
                            {notStartedArc > 0 && (
                                <circle cx="70" cy="70" r="56" stroke="#cbd5e1" strokeWidth="14" fill="none"
                                    strokeDasharray={`${notStartedArc} ${C - notStartedArc}`} strokeDashoffset={`-${completedArc + inProgressArc + overdueArc}`} strokeLinecap="round" />
                            )}
                        </svg>
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            <div className="text-2xl font-extrabold text-gray-900 leading-none tracking-tight">{total}</div>
                            <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest">TOTAL</div>
                        </div>
                    </div>
                    <div className="mt-3 space-y-1.5 text-xs w-full">
                        {[
                            { color: '#16a34a', label: 'Completed',   val: summary.completedCount,  cls: 'text-green-600' },
                            { color: '#2563eb', label: 'In Progress',  val: summary.inProgressCount, cls: 'text-blue-600' },
                            { color: '#dc2626', label: 'Overdue',      val: summary.overdueCount,    cls: 'text-red-600' },
                            { color: '#cbd5e1', label: 'Not Started',  val: summary.notStartedCount, cls: 'text-gray-400' },
                        ].map(({ color, label, val, cls }) => (
                            <div key={label} className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: color }} />
                                <span className="text-gray-600 flex-1">{label}</span>
                                <span className={`font-bold ${cls}`}>{val || 0}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Right — stat boxes + in-progress list */}
                <div>
                    <div className="grid grid-cols-4 gap-2 mb-4">
                        {[
                            { label: 'Completed',  val: summary.completedCount,  cls: 'text-green-600' },
                            { label: 'In Progress', val: summary.inProgressCount, cls: 'text-blue-600' },
                            { label: 'Overdue',    val: summary.overdueCount,    cls: 'text-red-600' },
                            { label: 'Not Started', val: summary.notStartedCount, cls: 'text-gray-400' },
                        ].map(({ label, val, cls }) => (
                            <div key={label} className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-center">
                                <div className={`text-xl font-extrabold tracking-tight ${cls}`}>{val || 0}</div>
                                <div className="text-xs text-gray-400 font-medium mt-0.5">{label}</div>
                            </div>
                        ))}
                    </div>

                    {summary.top_in_progress && summary.top_in_progress.length > 0 && (
                        <div>
                            <div className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">Top In-Progress</div>
                            <div className="space-y-2">
                                {summary.top_in_progress.slice(0, 5).map((rem) => (
                                    <div key={rem.id} className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-3">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold border flex-shrink-0 ${getRiskBadgeClass(rem.priority)}`}>{rem.priority}</span>
                                        <span className="text-xs font-semibold text-gray-800 flex-1 truncate">{rem.title}</span>
                                        <div className="w-20 flex-shrink-0">
                                            <div className="h-1 bg-gray-200 rounded-full overflow-hidden">
                                                <div className="h-full bg-blue-500 rounded-full" style={{ width: `${rem.progress_percentage || 0}%` }} />
                                            </div>
                                            <div className="text-xs text-gray-400 text-right mt-0.5">{rem.progress_percentage || 0}%</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Card>
    );
};

// Remediation Update Modal Component
const RemediationUpdateModal = ({ remediation, onClose, onUpdate }) => {
    const [formData, setFormData] = useState({
        status: remediation.status || 'Not Started',
        progress_percentage: remediation.progress_percentage || 0,
        assigned_to: remediation.assigned_to || '',
        comments: ''
    });
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const { user } = useAuth();
    const apiCall = useApi();

    // Fetch users for assignment dropdown
    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await apiCall('GET', '/users');
            setUsers(response || []);
        } catch (error) {
            setUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onUpdate(remediation.id, formData);
    };

    const statusOptions = [
        'Not Started',
        'In Progress',
        'Waiting',
        'Under Review',
        'Completed',
    ];

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
                <div className="mt-3">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-content-primary">Update Remediation</h3>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Status
                            </label>
                            <select
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                            >
                                {statusOptions.map(status => (
                                    <option key={status} value={status}>{status}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Progress Percentage: {formData.progress_percentage}%
                            </label>
                            <input
                                type="range"
                                min="0"
                                max="100"
                                value={formData.progress_percentage}
                                onChange={(e) => setFormData({ ...formData, progress_percentage: parseInt(e.target.value) })}
                                className="w-full"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Assigned To
                            </label>
                            <select
                                value={formData.assigned_to}
                                onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                                disabled={loadingUsers}
                            >
                                <option value="">Unassigned</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>
                                        {u.full_name || u.email}
                                    </option>
                                ))}
                            </select>
                            {loadingUsers && (
                                <p className="text-sm text-gray-500 mt-1">Loading users...</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Comment (Optional)
                            </label>
                            <textarea
                                value={formData.comments}
                                onChange={(e) => setFormData({ ...formData, comments: e.target.value })}
                                placeholder="Add a comment about this update..."
                                rows="3"
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                        </div>

                        <div className="flex justify-end space-x-3">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Update
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

// Recent Activity Card
const RecentActivityCard = ({ analytics }) => {
    const complianceStatus = analytics?.compliance_status || { 'SOC2': 85, 'ISO27001': 72, 'GDPR': 90, 'NIST': 68 };
    const activities = [
        {
            type: 'assessment_completion',
            title: 'New Assessment Completed',
            description: `${analytics?.recent_assessments || 0} assessments completed this month`,
            time: '2 hours ago',
            status: 'success'
        },
        {
            type: 'risk_update',
            title: 'Risk Rating Updated',
            description: 'Multiple risks reassessed and updated',
            time: '1 day ago',
            status: 'warning'
        },
        {
            type: 'compliance_check',
            title: 'Compliance Status Updated',
            description: `Framework compliance at ${Math.round(Object.values(complianceStatus).reduce((a, b) => a + b, 0) / Object.values(complianceStatus).length)}%`,
            time: '2 days ago',
            status: 'info'
        }
    ];

    const getDotColor = (status) => {
        switch (status) {
            case 'success': return 'bg-green-500';
            case 'warning': return 'bg-yellow-500';
            case 'info':    return 'bg-sky-500';
            default:        return 'bg-gray-400';
        }
    };

    const getBadgeClass = (status) => {
        switch (status) {
            case 'success': return 'bg-green-50 text-green-600 border-green-200';
            case 'warning': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
            case 'info':    return 'bg-sky-50 text-sky-600 border-sky-200';
            default:        return 'bg-gray-50 text-gray-500 border-gray-200';
        }
    };

    const getBadgeLabel = (status) => {
        switch (status) {
            case 'success': return 'Done';
            case 'warning': return 'Warning';
            case 'info':    return 'Info';
            default:        return 'Update';
        }
    };

    return (
        <Card className="relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(90deg,#0ea5e9,#8b5cf6)' }} />
            <div className="flex items-center justify-between mb-5 mt-1">
                <SectionTitle>Recent Activity</SectionTitle>
            </div>
            <div className="space-y-0">
                {activities.map((activity, index) => (
                    <div key={index} className="flex items-start gap-3 py-3 border-b border-gray-100 last:border-b-0">
                        <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${getDotColor(activity.status)}`} />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-gray-900">{activity.title}</span>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-bold border flex-shrink-0 ${getBadgeClass(activity.status)}`}>
                                    {getBadgeLabel(activity.status)}
                                </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">{activity.description}</p>
                            <p className="text-xs text-gray-400 mt-0.5">{activity.time}</p>
                        </div>
                    </div>
                ))}
            </div>
        </Card>
    );
};


// ─── Module Dashboard Clickable Cards ────────────────────────────────────────

const getModuleRiskLevel = (score) => severityPostureLevel(score);

const getModuleRiskBadgeColor = (level) => {
    switch (level) {
        case 'CRITICAL': return 'text-red-700 bg-red-100';
        case 'HIGH': return 'text-orange-600 bg-orange-100';
        case 'MEDIUM': return 'text-yellow-600 bg-yellow-100';
        case 'LOW': return 'text-green-600 bg-green-100';
        default: return 'text-gray-600 bg-gray-100';
    }
};

const getModuleStrokeColor = (level) => {
    switch (level) {
        case 'CRITICAL': return '#b91c1c';
        case 'HIGH': return '#ea580c';
        case 'MEDIUM': return '#ca8a04';
        default: return '#16a34a';
    }
};

// ── 4 Module Cards — all use shared ModuleCard component ─────────────────────
const EnterpriseModuleCard = ({ moduleStatus }) => {
    // "Ent" already combines org-level + deal-level Enterprise risks (see moduleStatus build).
    const ent = moduleStatus?.find(m => m.name === 'Ent') || {};
    return <ModuleCard name="Enterprise" icon="🏢" sub="Vulnerability Assessment" route="/EnterpriseDashboard" score={ent.score || 0} total={ent.total || 0} />;
};

const TPRMModuleCard = ({ moduleStatus }) => {
    const m = moduleStatus?.find(m => m.name === 'TPRM') || {};
    return <ModuleCard name="TPRM" icon="🤝" sub="Third-Party Risk" route="/TPRMDashboard" score={m.score || 0} total={m.total || 0} />;
};

const ExternalRisksModuleCard = ({ moduleStatus }) => {
    // No dedicated External dashboard — deep-link to the Risks page filtered to External events.
    const m = moduleStatus?.find(m => m.name === 'External') || {};
    return <ModuleCard name="External Risks" icon="🌐" sub="Integration-Sourced" route="/risks?types=External" score={m.score || 0} total={m.total || 0} cta="View External Risks" />;
};

const SpeedoModuleCard = ({ name, icon, sub, route, score, total, segmentColors, scoreColor }) => {
    const navigate = useNavigate();
    const level = severityPostureLevel(score);
    return (
        <div
            className="bg-white border border-gray-200 rounded-2xl p-5 cursor-pointer relative transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:border-gray-300"
            onClick={() => navigate(route)}
        >
            {/* Top accent bar — no overflow-hidden needed on parent since bar is inset */}
            <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl" style={{ background: 'linear-gradient(90deg, #0ea5e9, #8b5cf6)' }} />

            {/* Header */}
            <div className="flex items-start justify-between mb-1 mt-1">
                <div>
                    <div className="font-bold text-gray-900 text-sm">{name}</div>
                    <div className="text-xs text-gray-400 mt-0.5">{sub}</div>
                </div>
                <div className="w-10 h-10 flex items-center justify-center bg-gray-50 border border-gray-200 rounded-xl text-lg flex-shrink-0">{icon}</div>
            </div>

            {/* Gauge — smaller SVG (175×100); clip at 85px hides only the text strip
                  at the bottom while keeping the full arc + needle visible            */}
            <div className="flex justify-center" style={{ overflow: 'hidden', height: '85px' }}>
                <ReactSpeedometer
                    maxValue={100}
                    value={score}
                    needleColor="#1f2937"
                    customSegmentStops={[0, 25, 50, 75, 100]}
                    segmentColors={segmentColors}
                    segments={4}
                    width={175}
                    height={100}
                    ringWidth={20}
                    needleTransitionDuration={1000}
                    needleTransition="easeElastic"
                    currentValueText=" "
                    needleHeightRatio={0.5}
                    textColor="transparent"
                />
            </div>

            {/* Score % — standalone HTML, always below the clipped gauge */}
            <div className="text-center mt-2 mb-1">
                <span className="text-xl font-extrabold tracking-tight" style={{ color: total > 0 ? scoreColor : '#9ca3af' }}>{total > 0 ? `${score}%` : '—'}</span>
            </div>

            {/* Footer */}
            {total > 0 ? (
                <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">{total} active risks</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${getRiskBadgeClass(level)}`}>{level}</span>
                </div>
            ) : (
                <div className="text-xs text-gray-400 italic">Assessment Not Started</div>
            )}
            <div className="mt-1 text-xs text-gray-400">→ View {name} Dashboard</div>
        </div>
    );
};

const RansomwareModuleCard = ({ moduleStatus }) => {
    const m = moduleStatus?.find(m => m.name === 'Ransomware') || {};
    const score = m.score || 0;
    // Ransomware: vivid traffic-light — bright lime → lemon yellow → vivid orange → bright red
    const segmentColors = ["#4ade80", "#facc15", "#f97316", "#ef4444"];
    const scoreColor = score >= 75 ? "#ef4444" : score >= 50 ? "#f97316" : score >= 25 ? "#facc15" : "#4ade80";
    return <SpeedoModuleCard name="Ransomware" icon="🔒" sub="Threat Analysis" route="/RansomwareDashboard" score={score} total={m.total || 0} segmentColors={segmentColors} scoreColor={scoreColor} />;
};

const DarkWebModuleCard = ({ moduleStatus }) => {
    const m = moduleStatus?.find(m => m.name === 'Dark Web') || {};
    const score = m.score || 0;
    // Dark Web: cyber indigo → deep indigo → dark indigo → pure black (black touch at high risk)
    const segmentColors = ["#818cf8", "#4f46e5", "#3730a3", "#000000"];
    const scoreColor = score >= 75 ? "#000000" : score >= 50 ? "#3730a3" : score >= 25 ? "#4f46e5" : "#6366f1";
    return <SpeedoModuleCard name="Dark Web" icon="👁" sub="Exposure Monitoring" route="/DarkWebDashboard" score={score} total={m.total || 0} segmentColors={segmentColors} scoreColor={scoreColor} />;
};

const AIModuleCard = ({ moduleStatus }) => {
    const m = moduleStatus?.find(m => m.name === 'AI Risk') || {};
    return <ModuleCard name="AI Risk" icon="🤖" sub="AI/ML Assessment" route="/AIDashboard" score={m.score || 0} total={m.total || 0} />;
};

const GRCModuleCard = ({ moduleStatus }) => {
    const m = moduleStatus?.find(m => m.name === 'GRC') || {};
    return <ModuleCard name="GRC" icon="📋" sub="Governance & Compliance" route="/SecurityInsights" score={m.score || 0} total={m.total || 0} />;
};

// Risk Vector Readiness Card
/*
const ZeroTrustReadinessCard = ({ analytics }) => {
    const avgReadiness = Math.round(
        Object.values(analytics.zero_trust_readiness).reduce((a, b) => a + b, 0) /
        Object.values(analytics.zero_trust_readiness).length
    );

    return (
        <Card className="text-center">
            <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-400 uppercase tracking-wide">Risk Category</h3>
            </div>

            <div className="mb-4">
                <div className="text-3xl font-bold text-blue-600">{avgReadiness}%</div>
                <div className="text-xs text-gray-500">Risk Category</div>
            </div>
        </Card>
    );
};
*/

export default Dashboard;
